import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Paper, Alert } from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { useDealerAdmin } from '../contexts/DealerAdminContext';
import { dealerAdminReturnPath } from './Admin/dealerAdminNav';
import { authenticatedFetch } from '../api/apiClient';
import { toast } from 'react-toastify';
import { logOut } from '../services/authService';
import { ROUTES } from '../utils/constants';

/**
 * Unauthorized access page with optional "Request Access" form for logged-in users
 */
const UnauthorizedPage = () => {
  const { currentUser, userData } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const navigate = useNavigate();
  const [requestSent, setRequestSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: userData?.displayName || currentUser?.displayName || '',
    organization: '',
    reason: '',
  });

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!currentUser) return;

    setIsSubmitting(true);
    try {
      const response = await authenticatedFetch('/api/users/request-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: currentUser.uid,
          email: currentUser.email,
          name: formData.name,
          organization: formData.organization,
          reason: formData.reason,
        }),
      });

      if (response.ok) {
        setRequestSent(true);
        toast.success('Access request submitted!');
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to submit request');
      }
    } catch (err) {
      console.error('Error submitting access request:', err);
      toast.error('Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoToLogin = async () => {
    try {
      await logOut();
    } catch (err) {
      console.error('Error during logout before navigating to login:', err);
    } finally {
      navigate(ROUTES.LOGIN, { replace: true });
    }
  };

  if (dealerAdmin.active) {
    return (
      <Navigate
        to={dealerAdminReturnPath({ dealerId: dealerAdmin.dealerId, tenantId: dealerAdmin.tenantId })}
        replace
      />
    );
  }

  return (
    <MainLayout minimalHeader hideSupportButton>
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, px: 2 }}>
        {/* Access Denied Message */}
        <Paper sx={{ p: 4, mb: 3, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
          <Typography variant="h5" sx={{ color: '#b91c1c', fontWeight: 'bold', mb: 1 }}>
            Access Denied
          </Typography>
          <Typography sx={{ color: '#dc2626' }}>
            You do not have permission to access this page. Your account may not be set up yet.
          </Typography>
        </Paper>

        {/* Request Access Form - only show if logged in and request not yet sent */}
        {currentUser && !requestSent && (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Request Access
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Fill out this form to request access. An administrator will review your request.
            </Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Your Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Organization / Company"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Why do you need access?"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                multiline
                rows={3}
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || !formData.name}
                fullWidth
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </form>
          </Paper>
        )}

        {/* Success message after request sent */}
        {requestSent && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Your access request has been submitted. An administrator will review it and contact you
            at <strong>{currentUser?.email}</strong>.
          </Alert>
        )}

        {/* Message for non-logged-in users */}
        {!currentUser && (
          <Paper sx={{ p: 4, mt: 3 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              If you believe you should have access, please contact your administrator.
            </Typography>
          </Paper>
        )}

        {currentUser && (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              If your access has now been approved, click below to return to the sign-in page and
              log in again. You will be redirected to your home page after a successful login.
            </Typography>
            <Button variant="outlined" onClick={handleGoToLogin}>
              Go to Login
            </Button>
          </Box>
        )}
      </Box>
    </MainLayout>
  );
};

export default UnauthorizedPage;
